/** Chat sharing types. Access is per user only — no team or department sharing. */

export const ChatAccessType = {
  VIEWER: 'VIEWER',
  COLLABORATOR: 'COLLABORATOR',
} as const;

export type ChatAccessType = (typeof ChatAccessType)[keyof typeof ChatAccessType];

export const ChatAccessTypeLabels: Record<ChatAccessType, string> = {
  VIEWER: 'Viewer',
  COLLABORATOR: 'Collaborator',
};

/** Sentinel the backend expects instead of an access type when revoking an existing invite. */
export const CHAT_ACCESS_DELETE = 'DELETE';

/** Someone the chat is currently shared with. */
export interface ChatInvitedUser {
  userId: string;
  name: string;
  accessType: ChatAccessType;
}

/** Someone the current user may share this chat with — their own department only. */
export interface ChatShareCandidate {
  userId: string;
  name: string;
  /** Shown after the name when another candidate has the same name. */
  email?: string;
}

export interface GetChatInvitedUsersRequest {
  chatId: string;
}

export interface GetChatInvitedUsersResponse {
  users: ChatInvitedUser[];
}

export interface GetChatShareCandidatesResponse {
  candidates: ChatShareCandidate[];
}

/** One access change: a level to grant or update to, or `DELETE` to revoke an existing invite. */
export interface ChatUserAccess {
  userId: string;
  accessType: ChatAccessType | typeof CHAT_ACCESS_DELETE;
}

export interface SetChatUserAccessRequest {
  chatId: string;
  /** Only the changes — the backend rejects an empty list. */
  users: ChatUserAccess[];
}

export interface SetChatUserAccessResponse {
  chatId: string;
  users: { userId: string; accessType: ChatAccessType | null }[];
}

/** Access changes to send for an edited list. Removed users get `DELETE`; an empty result means nothing to save. */
export const diffChatUserAccess = (current: ChatInvitedUser[], next: ChatInvitedUser[]): ChatUserAccess[] => {
  const currentByUserId = new Map(current.map((user) => [user.userId, user]));
  const nextUserIds = new Set(next.map((user) => user.userId));

  const changes: ChatUserAccess[] = next
    .filter((user) => currentByUserId.get(user.userId)?.accessType !== user.accessType)
    .map(({ userId, accessType }) => ({ userId, accessType }));

  return changes.concat(
    current
      .filter((user) => !nextUserIds.has(user.userId))
      .map(({ userId }) => ({ userId, accessType: CHAT_ACCESS_DELETE })),
  );
};
